import { useSavedListStore } from "../../store/savedListStore";
import { useListStore, ListWithProfile } from "../../store/listStore";
import { usePlaceStore } from "../../store/placeStore";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/joy";
import React from "react";
import { useNavigate } from "react-router-dom";

type ViewListProps = {
  listId: string;
  onUnsave: () => void;
  onPlaceClick?: (placeId: string) => void;
};

export const ViewList = ({ listId, onUnsave, onPlaceClick }: ViewListProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { savedLists, unsaveList, saveList, isListSaved } =
    useSavedListStore();
  const { fetchList } = useListStore();
  const { listPlaces, fetchListPlaces } = usePlaceStore();

  const saved = savedLists.find((s) => s.list_id === listId);

  const [directList, setDirectList] = React.useState<ListWithProfile | null>(
    null,
  );
  const [loading, setLoading] = React.useState(false);
  const [fetchFailed, setFetchFailed] = React.useState(false);

  React.useEffect(() => {
    fetchListPlaces(listId);
  }, [listId, fetchListPlaces]);

  // Fallback: fetch list directly if not in savedLists
  React.useEffect(() => {
    if (saved) return;

    setLoading(true);
    setFetchFailed(false);
    fetchList(listId).then((data) => {
      setDirectList(data);
      setFetchFailed(!data);
      setLoading(false);
    });
  }, [listId, saved, fetchList]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        marginTop={theme.spacing(10)}
      >
        <CircularProgress data-testid="view-list-loading" />
      </Box>
    );
  }

  // Use saved list data or direct-fetched data
  const listData = saved
    ? saved.list
    : directList;

  if (!listData || fetchFailed) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop={theme.spacing(10)}
      >
        <SentimentDissatisfiedIcon
          fontSize="large"
          data-testid="list-not-found-icon"
        />
        <Typography>Oops, list not found!</Typography>
      </Box>
    );
  }

  const isSaved = isListSaved(listId);

  const handleToggleSave = async () => {
    if (isSaved) {
      await unsaveList(listId);
      onUnsave();
    } else {
      await saveList(listId);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack paddingLeft={theme.spacing(2)} paddingRight={theme.spacing(2)}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Typography
            level="h4"
            sx={{ fontWeight: theme.fontWeight.lg }}
            data-testid="view-list-title"
          >
            {listData.title}
          </Typography>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={handleToggleSave}
            aria-label={isSaved ? "Unsave list" : "Save list"}
            data-testid={isSaved ? "unsave-list-button" : "save-list-button"}
          >
            {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </IconButton>
        </Stack>

        {listData.description && (
          <Typography
            level="body-sm"
            sx={{ color: "text.secondary", whiteSpace: "pre-line" }}
            data-testid="view-list-description"
          >
            {listData.description}
          </Typography>
        )}

        <Typography
          level="body-sm"
          sx={{ color: "text.secondary" }}
          data-testid="view-list-creator"
        >
          by{" "}
          <Typography
            component="span"
            sx={{
              cursor: "pointer",
              color: "primary.500",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => navigate(`/profile/${listData.user_id}`)}
          >
            @{listData.profiles?.username}
          </Typography>
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          {listData.privacy_level === "public" ? (
            <PublicIcon fontSize="small" />
          ) : (
            <LockIcon fontSize="small" />
          )}
          <Typography level="body-sm">
            {listData.privacy_level === "public" ? "Public" : "Private"}
          </Typography>
        </Stack>
      </Stack>

      <Divider />

      <List>
        {listPlaces.map((place) => (
          <ListItemButton
            key={place.id}
            onClick={() => onPlaceClick?.(place.id)}
            sx={{ padding: `${theme.spacing(1)} ${theme.spacing(3)}` }}
            data-testid={`view-place-item-${place.id}`}
          >
            {place.place_name}
          </ListItemButton>
        ))}
      </List>
    </Stack>
  );
};
